import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../util/classNameConstructor";
import ToggleInputGroup from "../../molecules/toggleInputGroup/ToggleInputGroup";
import type { ChartsProps } from "./chart.types";
import DateChart from "./DateChart";
import { DAY, HOUR } from "./helpers";

interface ChartWrapperProps
  extends Omit<ChartsProps, "data">,
    React.HTMLAttributes<HTMLDivElement> {
  data: any[];
  showHeader?: boolean;
  showToggles?: boolean;
  startDate?: number;
  endDate?: number;
}
const getUNIXDay = (date: string) => {
  const d = new Date(date);
  const time = d.getTime();
  d.setHours(d.getHours(), 0, 0, 0);
  const hour = Math.ceil(d.getTime() / HOUR);
  d.setHours(0, 0, 0, 0);
  const day = Math.ceil(d.getTime() / DAY);
  return { time, hour, day };
};

const DateChartWrapper = forwardRef<HTMLDivElement, ChartWrapperProps>(
  (
    {
      data,
      showHeader,
      showToggles = true,
      className,
      startDate,
      endDate,
      ...props
    },
    ref,
  ) => {
    console.log("data", data, props);
    const { t } = useTranslation();
    const keys = Object.keys(props.dataKeys);
    const [checkboxState, setCheckboxState] = useState(
      Object.fromEntries(keys.map((key) => [key, true])),
    );
    const addDataPoint = (acc, accGroup, accKey, key, value) => {
      acc[accGroup][accKey] = {
        ...value,
        ...acc[accGroup][accKey],
        [key]: (acc[accGroup][accKey]?.[key] || 0) + 1,
      };
    };

    const fillDates = (d, interval) => {
      const last = Number.parseInt(
        Object.keys(d)[Object.keys(d).length - 1],
        10,
      );
      const first = Number.parseInt(Object.keys(d)[0], 10) - 1;
      const start = Math.min(
        startDate ? Math.round(startDate / interval) : Number.POSITIVE_INFINITY,
        first,
      );
      const end = Math.min(
        new Date().getTime() / interval,
        endDate ? Math.round(endDate / interval) : last,
      );

      for (let i = start; i <= end; i++) {
        Object.assign(d, {
          [i]: {
            ...keys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {}),
            ...d[i],
            date: i * DAY,
          },
        });
      }
    };

    const { values, dataByDay } = keys.reduce(
      (accumulator, key) => {
        data[key]?.forEach((value) => {
          const { time: date, day } = getUNIXDay(value.createdAt);
          accumulator.values[key] = (accumulator.values[key] || 0) + 1;

          addDataPoint(accumulator, "dataByDay", day, key, { date });
        });

        return accumulator;
      },
      { values: {}, dataByHour: {}, dataByDay: {} },
    );
    fillDates(dataByDay, DAY);

    const isEmpty = (obj) =>
      Object.keys(obj).length === 0 && obj.constructor === Object;

    return (
      <div className="flex h-full flex-col gap-5">
        {isEmpty(dataByDay) ? (
          <div className="bg-surface-subdued mb-5 flex w-full grow items-center justify-center rounded-xl p-5">
            <span className="body-md text-text-subdued text-center">
              {t("util:nothingToShowYet", {
                val: t("global:data"),
              })}
            </span>
          </div>
        ) : (
          <DateChart
            {...props}
            dataKeys={Object.fromEntries(
              Object.entries(props.dataKeys).filter(
                ([key]) => checkboxState[key],
              ),
            )}
            checkboxState={checkboxState}
            data={Object.values(dataByDay)}
            className={cn("z-10", className)}
            showHeader={showHeader}
            ref={ref}
          />
        )}
        {!isEmpty(dataByDay) && showToggles && keys.length > 1 && (
          <div className="relative">
            <ToggleInputGroup
              items={Object.entries(props.dataKeys).map(([key, value]) => ({
                ...value,
                name: key,
                value: values[key],
                checked: checkboxState[key],
                receiveValue: () =>
                  setCheckboxState({
                    ...checkboxState,
                    [key]: !checkboxState[key],
                  }),
              }))}
            />
          </div>
        )}
      </div>
    );
  },
);

DateChartWrapper.displayName = "DateChartWrapper";
export default DateChartWrapper;
